import React, { useState, useRef, useEffect, useMemo } from 'react';
import { LTSSService } from '../../types/ltss';
import { Header } from './Header';
import { KPICards } from './KPICards';
import { TabNavigation, TabType } from './TabNavigation';
import { DropZone } from '../FileUpload/DropZone';
import { AlertList } from '../Alerts/AlertList';
import { AlertSummary } from '../Alerts/AlertSummary';
import { StaffTable } from '../Staff/StaffTable';
import { ClientTable } from '../Clients/ClientTable';
import { RevenueSummary } from '../Financial/RevenueSummary';
import { ClaimsStatus } from '../Financial/ClaimsStatus';
import { RevenueChart } from '../Financial/RevenueChart';
import { HistoryPanel } from '../History/HistoryPanel';
import { SettingsPanel } from '../Settings/SettingsPanel';
import { PayrollReport } from '../Payroll/PayrollReport';
import { PDFExportButton } from '../Reports/PDFExportButton';
import { AuditReportButton } from '../Reports/AuditReportButton';
import { SearchBar, SearchSuggestion } from '../Search/SearchBar';
import { SearchResults } from '../Search/SearchResults';
import { FeatureLock } from '../PlanGate/FeatureLock';
import { GoogleDriveButton } from '../CloudStorage/GoogleDriveButton';
import { useAlerts } from '../../hooks/useAlerts';
import { useStaffMetrics } from '../../hooks/useStaffMetrics';
import { useClientUtilization } from '../../hooks/useClientUtilization';
import { useFinancials } from '../../hooks/useFinancials';
import { useHistoricalData } from '../../hooks/useHistoricalData';
import { useWebhook } from '../../hooks/useWebhook';
import { usePlanFeatures } from '../../hooks/usePlanFeatures';
import { useLocationFilter } from '../../hooks/useLocationFilter';
import { generateAuditReportBlob } from '../../utils/auditReportGenerator';

interface DashboardProps {
  services: LTSSService[];
  fileName: string | null;
  isLoading: boolean;
  onFileSelect: (file: File) => void;
  onClear: () => void;
  onLoadServices?: (services: LTSSService[], fileName: string) => void;
  userEmail?: string;
  plan?: string;
  onUpgradeClick?: () => void;
  onLogout?: () => void;
}

export function Dashboard({ services, fileName, isLoading, onFileSelect, onClear, onLoadServices, userEmail, plan, onUpgradeClick, onLogout }: DashboardProps) {
  const [activeTab, setActiveTab] = useState<TabType>('alerts');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [hasSavedCurrentReport, setHasSavedCurrentReport] = useState(false);
  // Plan features available for future gating
  usePlanFeatures();
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchType, setSearchType] = useState<'staff' | 'client' | null>(null);
  const [isSearchActive, setIsSearchActive] = useState(false);

  // Filter services by location (Enterprise feature)
  const filteredServices = useLocationFilter(services);

  const { alerts, summary } = useAlerts(filteredServices);
  const staffMetrics = useStaffMetrics(filteredServices);
  const clientUtilization = useClientUtilization(filteredServices);
  const financials = useFinancials(filteredServices, clientUtilization);

  // Extract unique staff names for settings
  const knownStaffNames = useMemo(() => {
    const names: string[] = [];
    const seen: Record<string, boolean> = {};
    staffMetrics.forEach(s => {
      if (!seen[s.staffName]) {
        seen[s.staffName] = true;
        names.push(s.staffName);
      }
    });
    return names.sort();
  }, [staffMetrics]);
  
  const {
    reports,
    trendData,
    saveCurrentReport,
    loadReport,
    removeReport,
  } = useHistoricalData();

  const {
    config: webhookConfig,
    isLoading: webhookLoading,
    lastResult: webhookLastResult,
    updateConfig: updateWebhookConfig,
    testWebhook,
    triggerWebhook,
  } = useWebhook();

  // Auto-save report and trigger webhook when new data is loaded
  useEffect(() => {
    if (services.length > 0 && fileName && !hasSavedCurrentReport) {
      // Save to IndexedDB
      saveCurrentReport(
        fileName,
        services,
        alerts,
        financials,
        staffMetrics.length,
        clientUtilization.length
      );
      
      // Trigger webhook if configured
      triggerWebhook(
        fileName,
        services,
        alerts,
        financials,
        staffMetrics.length,
        clientUtilization.length
      );
      
      setHasSavedCurrentReport(true);
    }
  }, [services, fileName, alerts, financials, staffMetrics.length, clientUtilization.length, hasSavedCurrentReport, saveCurrentReport, triggerWebhook]);

  // Reset save flag when data is cleared
  useEffect(() => {
    if (services.length === 0) {
      setHasSavedCurrentReport(false);
    }
  }, [services.length]);

  // Generate search suggestions from services
  const searchSuggestions: SearchSuggestion[] = React.useMemo(() => {
    const staffCounts: Record<string, number> = {};
    const clientCounts: Record<string, number> = {};
    
    services.forEach(s => {
      if (s.staffName) {
        staffCounts[s.staffName] = (staffCounts[s.staffName] || 0) + 1;
      }
      if (s.clientName) {
        clientCounts[s.clientName] = (clientCounts[s.clientName] || 0) + 1;
      }
    });
    
    const suggestions: SearchSuggestion[] = [
      ...Object.entries(staffCounts).map(([name, count]) => ({
        type: 'staff' as const,
        name,
        count,
      })),
      ...Object.entries(clientCounts).map(([name, count]) => ({
        type: 'client' as const,
        name,
        count,
      })),
    ];
    
    return suggestions.sort((a, b) => b.count - a.count);
  }, [services]);

  const handleSearch = (query: string, type: 'staff' | 'client' | null) => {
    setSearchQuery(query);
    setSearchType(type);
    setIsSearchActive(true);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setSearchType(null);
    setIsSearchActive(false);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _loadReportFromHistory = loadReport;
  
  const handleLoadReport = React.useCallback(async (report: { services: LTSSService[]; fileName: string }) => {
    if (onLoadServices) {
      setHasSavedCurrentReport(true); // Don't re-save loaded reports
      onLoadServices(report.services, report.fileName);
      setActiveTab('alerts'); // Switch to alerts tab after loading
    }
  }, [onLoadServices, setActiveTab]);

  const handleUploadClick = () => {
    // If we have data loaded, clear it to go back to dashboard/upload screen
    if (services.length > 0) {
      onClear();
    } else {
      // If no data, open file picker
      fileInputRef.current?.click();
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      onFileSelect(file);
    }
  };

  // Show upload screen if no data and not viewing settings
  if (services.length === 0 && !isLoading && activeTab !== 'settings' && activeTab !== 'history') {
    return (
      <div className="min-h-screen bg-slate-900 flex flex-col">
        <Header fileName={null} onUploadClick={handleUploadClick} onClear={onClear} userEmail={userEmail} plan={plan} onUpgradeClick={onUpgradeClick} onLogout={onLogout} />
        <input
          ref={fileInputRef}
          type="file"
          accept=".xls,.xlsx,.csv"
          onChange={handleFileChange}
          className="hidden"
        />
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="max-w-lg w-full">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold gradient-text mb-2">
                LTSS Analytics Dashboard
              </h2>
              <p className="text-slate-400">
                Upload your Maryland LTSS EVV Services Rendered Report to get started
              </p>
            </div>
            <DropZone onFileSelect={onFileSelect} isLoading={isLoading} />
            
            {/* Google Drive Import */}
            <div className="mt-4 flex items-center justify-center gap-3">
              <div className="h-px bg-slate-700 flex-1" />
              <span className="text-xs text-slate-500">or import from</span>
              <div className="h-px bg-slate-700 flex-1" />
            </div>
            <div className="mt-4 flex justify-center">
              <GoogleDriveButton
                mode="load"
                loadLabel="Import from Google Drive"
                onLoad={onFileSelect}
              />
            </div>
            
            <div className="mt-6 text-center">
              <p className="text-xs text-slate-500">
                All data is processed locally in your browser. No data is sent to any server.
              </p>
            </div>
            
            {/* Quick access to Settings and History */}
            <div className="mt-8 flex justify-center gap-4">
              <button
                onClick={() => setActiveTab('settings')}
                className="flex items-center gap-2 px-4 py-2 text-sm text-slate-400 
                         hover:text-slate-200 bg-slate-800/50 hover:bg-slate-800 
                         rounded-lg transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                    d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Settings
              </button>
              {reports.length > 0 && (
                <button
                  onClick={() => setActiveTab('history')}
                  className="flex items-center gap-2 px-4 py-2 text-sm text-slate-400 
                           hover:text-slate-200 bg-slate-800/50 hover:bg-slate-800 
                           rounded-lg transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  History ({reports.length})
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show settings or history panel when no data but tab is selected
  if (services.length === 0 && !isLoading && (activeTab === 'settings' || activeTab === 'history')) {
    return (
      <div className="min-h-screen bg-slate-900 flex flex-col">
        <Header fileName={null} onUploadClick={handleUploadClick} onClear={onClear} userEmail={userEmail} plan={plan} onUpgradeClick={onUpgradeClick} onLogout={onLogout} />
        <input
          ref={fileInputRef}
          type="file"
          accept=".xls,.xlsx,.csv"
          onChange={handleFileChange}
          className="hidden"
        />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 w-full">
          {/* Back button */}
          <button
            onClick={() => setActiveTab('alerts')}
            className="flex items-center gap-2 text-slate-400 hover:text-slate-200 mb-6 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Upload
          </button>
          
          <div className="card p-6">
            {activeTab === 'settings' && (
              <SettingsPanel
                webhookConfig={webhookConfig}
                webhookLoading={webhookLoading}
                webhookLastResult={webhookLastResult}
                onUpdateWebhookConfig={updateWebhookConfig}
                onTestWebhook={testWebhook}
                knownStaffNames={knownStaffNames}
              />
            )}
            {activeTab === 'history' && (
              <HistoryPanel
                reports={reports}
                trendData={trendData}
                onLoadReport={handleLoadReport}
                onDeleteReport={removeReport}
              />
            )}
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900">
      <Header fileName={fileName} onUploadClick={handleUploadClick} onClear={onClear} userEmail={userEmail} plan={plan} onUpgradeClick={onUpgradeClick} onLogout={onLogout} />
      <input
        ref={fileInputRef}
        type="file"
        accept=".xls,.xlsx,.csv"
        onChange={handleFileChange}
        className="hidden"
      />

      <main className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-6">
        {/* Top Action Bar - Clean layout with grouped actions */}
        <div className="flex flex-col gap-3 mb-5 sm:mb-6">
          {/* Row 1: Search (full width on mobile, left-aligned on desktop) */}
          <div className="w-full sm:max-w-md">
            <SearchBar
              suggestions={searchSuggestions}
              onSearch={handleSearch}
              onClear={handleClearSearch}
              isActive={isSearchActive}
            />
          </div>
          
          {/* Row 2: Export Actions */}
          <div className="flex flex-wrap items-center gap-2">
            <FeatureLock feature="canExportPDF" onUpgradeClick={onUpgradeClick}>
              <div className="flex items-center gap-2 p-1 bg-slate-800/50 rounded-lg">
                {/* Google Drive */}
                <GoogleDriveButton
                  mode="save"
                  saveLabel="Google Drive"
                  onSave={async () => {
                    const blob = await generateAuditReportBlob({
                      fileName: fileName || 'report',
                      services,
                      alerts,
                      staffMetrics,
                      clientUtilization,
                      financials,
                    });
                    const reportName = `LTSS_Report_${new Date().toISOString().split('T')[0]}.pdf`;
                    return { blob, fileName: reportName };
                  }}
                />
                {/* PDF Export */}
                <PDFExportButton
                  data={{
                    fileName: fileName || 'report',
                    services,
                    alerts,
                    alertSummary: summary,
                    staffMetrics,
                    clientUtilization,
                    financials,
                  }}
                />
                {/* Audit Report */}
                <AuditReportButton
                  data={{
                    fileName: fileName || 'report',
                    services,
                    alerts,
                    staffMetrics,
                    clientUtilization,
                    financials,
                  }}
                />
              </div>
            </FeatureLock>
          </div>
        </div>

        {/* Search Results or Normal View */}
        {isSearchActive ? (
          <div className="mb-6">
            <SearchResults
              query={searchQuery}
              searchType={searchType}
              services={services}
              alerts={alerts}
              onClose={handleClearSearch}
            />
          </div>
        ) : (
          <>
            {/* KPI Cards */}
            <div className="mb-6">
              <KPICards
                totalServices={filteredServices.length}
                totalRevenue={financials.totalAmountPaid}
                activeStaff={staffMetrics.length}
                activeAlerts={summary.total}
                services={filteredServices}
                alerts={alerts}
                financials={financials}
                staffMetrics={staffMetrics}
                clientUtilization={clientUtilization}
              />
            </div>

        {/* Tab Navigation */}
        <div className="card mb-6">
          <TabNavigation
            activeTab={activeTab}
            onTabChange={setActiveTab}
            alertCount={summary.critical + summary.warning}
            historyCount={reports.length}
          />

          {/* Tab Content */}
          <div className="p-4">
            {activeTab === 'alerts' && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-1">
                  <AlertSummary
                    critical={summary.critical}
                    warning={summary.warning}
                    caution={summary.caution}
                    info={summary.info}
                  />
                </div>
                <div className="lg:col-span-2">
                  <AlertList alerts={alerts} onSearchPerson={handleSearch} />
                </div>
              </div>
            )}

            {activeTab === 'staff' && (
              <FeatureLock feature="canViewStaffMetrics" onUpgradeClick={onUpgradeClick}>
                <div>
                  <h3 className="text-lg font-medium text-slate-200 mb-4">
                    Staff Performance & Compliance
                  </h3>
                  <StaffTable staff={staffMetrics} />
                </div>
              </FeatureLock>
            )}

            {activeTab === 'clients' && (
              <div>
                <h3 className="text-lg font-medium text-slate-200 mb-4">
                  Client Utilization
                </h3>
                <ClientTable clients={clientUtilization} services={filteredServices} />
              </div>
            )}

            {activeTab === 'financial' && (
              <FeatureLock feature="canViewFinancials" onUpgradeClick={onUpgradeClick}>
                <div className="space-y-6">
                  <RevenueSummary financials={financials} clientUtilization={clientUtilization} services={filteredServices} />
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <ClaimsStatus financials={financials} />
                    <RevenueChart services={services} />
                  </div>
                </div>
              </FeatureLock>
            )}

            {activeTab === 'history' && (
              <HistoryPanel
                reports={reports}
                trendData={trendData}
                onLoadReport={handleLoadReport}
                onDeleteReport={removeReport}
              />
            )}

            {activeTab === 'payroll' && (
              <FeatureLock feature="canViewFinancials" onUpgradeClick={onUpgradeClick}>
                <PayrollReport staffMetrics={staffMetrics} />
              </FeatureLock>
            )}

            {activeTab === 'settings' && (
              <SettingsPanel
                webhookConfig={webhookConfig}
                webhookLoading={webhookLoading}
                webhookLastResult={webhookLastResult}
                onUpdateWebhookConfig={updateWebhookConfig}
                onTestWebhook={testWebhook}
                onUpgradeClick={onUpgradeClick}
                knownStaffNames={knownStaffNames}
                services={filteredServices}
              />
            )}
          </div>
        </div>
          </>
        )}
      </main>
    </div>
  );
}
